// Sketch event log — a debug/observability panel for the <cad-sketch> public
// event surface. Captures every CustomEvent whose type starts with `sketch-`
// or `cad-` by patching `dispatchEvent` on the element instance rather than
// listing event names, so new engine events are picked up without changes
// here. See EventLogPanel.vue for the UI.

import { readonly, ref, type Ref } from 'vue'

export interface EventLogEntry {
  id:     number
  ts:     number
  name:   string
  detail: unknown
}

/**
 * Three-state visibility:
 *   off       — no UI at all (default; opt-in via the account menu)
 *   collapsed — compact pill showing the running count
 *   visible   — full panel with the event stream
 */
export type EventLogMode = 'off' | 'collapsed' | 'visible'

const MAX_ENTRIES        = 500
const STORAGE_MODE_KEY   = 'sketch-event-log:mode'
const STORAGE_MIRROR_KEY = 'sketch-event-log:mirror-console'

const entries = ref<EventLogEntry[]>([])
const mode    = ref<EventLogMode>(readMode())
const mirror  = ref<boolean>(readMirror())

let nextId = 1

function readMode(): EventLogMode {
  try {
    const raw = sessionStorage.getItem(STORAGE_MODE_KEY)
    if (raw === 'collapsed' || raw === 'visible' || raw === 'off') return raw
  } catch { /* ignore */ }
  return 'off'
}

function readMirror(): boolean {
  try { return sessionStorage.getItem(STORAGE_MIRROR_KEY) === '1' } catch { return false }
}

function push(name: string, detail: unknown): void {
  const entry: EventLogEntry = { id: nextId++, ts: Date.now(), name, detail }

  // Bounded ring buffer — drop the oldest when we hit the cap, so a long
  // session with many events doesn't grow memory without bound.
  const next = entries.value.length >= MAX_ENTRIES
    ? entries.value.slice(entries.value.length - MAX_ENTRIES + 1)
    : entries.value.slice()
  next.push(entry)
  entries.value = next

  if (mirror.value) {
    // console.log rather than console.debug — DevTools hides debug-level
    // messages by default (requires "Verbose"), and the whole point of the
    // toggle is that flipping it should show output without extra setup.
    // eslint-disable-next-line no-console
    console.log(`[sketch-event] ${name}`, detail)
  }
}

// Events intentionally hidden from the log to cut noise. Both events are still
// part of the public API — the panel just doesn't display them. `sketch-tool-
// used` duplicates what `sketch-tool-changed` already carries for the panel's
// baseline-observation purpose.
const IGNORED_EVENTS = new Set<string>([
  'sketch-tool-used',
])

/**
 * Patch `dispatchEvent` on the given element so every CustomEvent whose type
 * starts with `sketch-` or `cad-` (and isn't in IGNORED_EVENTS) gets logged.
 * Returns a detach function that restores the original method. Safe to call
 * once per element mount.
 */
export function attach(el: HTMLElement): () => void {
  const original = el.dispatchEvent.bind(el)
  const patched = (event: Event): boolean => {
    if (
      event instanceof CustomEvent &&
      /^(sketch|cad)-/.test(event.type) &&
      !IGNORED_EVENTS.has(event.type)
    ) {
      push(event.type, event.detail)
    }
    return original(event)
  }
  el.dispatchEvent = patched

  return () => {
    // Only restore if nothing else has re-patched over us in the meantime.
    if (el.dispatchEvent === patched) el.dispatchEvent = original
  }
}

export function clear(): void {
  entries.value = []
}

export function setMode(value: EventLogMode): void {
  mode.value = value
  try { sessionStorage.setItem(STORAGE_MODE_KEY, value) } catch { /* ignore */ }
}

export function setMirror(value: boolean): void {
  mirror.value = value
  try { sessionStorage.setItem(STORAGE_MIRROR_KEY, value ? '1' : '0') } catch { /* ignore */ }
}

export function useEventLog(): {
  entries: Readonly<Ref<readonly EventLogEntry[]>>
  mode:    Readonly<Ref<EventLogMode>>
  mirror:  Readonly<Ref<boolean>>
} {
  return {
    entries: readonly(entries) as Readonly<Ref<readonly EventLogEntry[]>>,
    mode:    readonly(mode),
    mirror:  readonly(mirror),
  }
}
